import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useTranslation } from 'react-i18next';
import './reports.css';

const DailyAppointments = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchDoctors();
  }, [token, i18n.language]);

  useEffect(() => {
    if (selectedDoctor && selectedDate) {
      fetchDailyAppointments();
    }
  }, [selectedDoctor, selectedDate, token, i18n.language]);

  const fetchDoctors = async () => {
    try {
      const response = await fetch('https://naql.nozzm.com/api/doctores', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify({}),
      });

      const result = await response.json();
      if (result.status) {
        setDoctors(result.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const fetchDailyAppointments = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://naql.nozzm.com/api/daily_appointments_report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify({
          doctor_id: selectedDoctor,
          date: selectedDate
        }),
      });

      const result = await response.json();
      if (result.status) {
        setAppointments(result.data || []);
      } else {
        setError(result.message || 'Failed to fetch appointments');
      }
    } catch (error) {
      setError('Error fetching appointments');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA');
  };

  const formatTime = (timeString) => {
    return timeString;
  };


  return (
    <div className="container tables bg-white mt-5">
      <div className="tableTitle">
        <h3>{t('reports.dailyAppointments.title')}</h3>
      </div>

      <div className="report-filters">
        <div className="row">
          <div className="col-md-4">
            <label className="form-label">{t('reports.selectDoctor')}</label>
            <select
              className="form-control"
              value={selectedDoctor}
              onChange={(e) => setSelectedDoctor(e.target.value)}
            >
              <option value="">{t('reports.selectDoctor')}</option>
              {doctors.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('reports.selectDate')}</label>
            <input
              type="date"
              className="form-control"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {selectedDoctor && selectedDate && (
        <div className="report-summary">
          <div className="row">
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.totalAppointments')}</h5>
                <span className="summary-number">{appointments.length}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.completedAppointments')}</h5>
                <span className="summary-number">
                  {appointments.filter(apt => apt.status === 'completed').length}
                </span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.pendingAppointments')}</h5>
                <span className="summary-number">
                  {appointments.filter(apt => apt.status === 'pending').length}
                </span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.cancelledAppointments')}</h5>
                <span className="summary-number">
                  {appointments.filter(apt => apt.status === 'cancelled').length}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedDoctor && selectedDate && (
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>{t('reports.patientName')}</th>
                <th>{t('reports.appointmentTime')}</th>
                <th>{t('reports.service')}</th>
                <th>{t('reports.status')}</th>
                <th>{t('reports.notes')}</th>
              </tr>
            </thead>
            <tbody>
              {appointments.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    {t('reports.noAppointments')}
                  </td>
                </tr>
              ) : (
                appointments.map((appointment) => (
                  <tr key={appointment.id}>
                    <td>{appointment.patient_name}</td>
                    <td>{formatTime(appointment.appointment_time)}</td>
                    <td>{appointment.service_name}</td>
                    <td>
                      <span className={`badge bg-${appointment.status === 'completed' ? 'success' : 
                        appointment.status === 'pending' ? 'warning' : 'danger'}`}>
                        {t(`reports.status.${appointment.status}`)}
                      </span>
                    </td>
                    <td>{appointment.notes || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DailyAppointments;
